/** The webmail's directory for RecipientInput (composer, event attendees, sharing): GAL + contacts + recent. */
import { useErrorText, type RecipientDirectory } from '@platform/ui';
import { useMemo } from 'react';
import { api } from './api';

export function useRecipientDirectory(): RecipientDirectory {
  const errorText = useErrorText();
  return useMemo<RecipientDirectory>(
    () => ({
      suggest: async (query, signal) => {
        const base = await api.suggestRecipients(query, signal);
        // Optional DAV discovery cannot suppress ordinary GAL/contact autocomplete during an outage.
        const groups = await api.groupware.groups().catch(() => ({ groups: [] }));
        const matches = groups.groups.filter(group => group.name.toLocaleLowerCase().includes(query.toLocaleLowerCase())).slice(0, 5);
        const personal = await Promise.all(matches.map(async group => {
          const expanded = await api.groupware.expandGroup(group.id);
          if (!expanded.complete || !expanded.members.length) return [];
          return [{ key: `contact-group:${group.id}`, name: group.name, email: '', source: 'contact' as const, kind: 'group' as const,
            members: expanded.members.map(member => ({ key: member.email, email: member.email, name: null, source: 'contact' as const, kind: 'person' as const })) }];
        }));
        if (signal.aborted) return [];
        return [...base, ...personal.flat()];
      },
      resolve: (entries) => api.resolveRecipients(entries),
      removeSuggestion: (entry) => api.removeSuggestion(entry.key).catch(errorText.notify),
      expandGroup: (recipient) =>
        recipient.key
          ? api.groupMembers(recipient.key).catch((error: unknown) => {
              errorText.notify(error);
              return [];
            })
          : Promise.resolve([]),
      photoUrl: (email) => api.photoUrl(email),
    }),
    [errorText],
  );
}
